import React from 'react';

type SuggestionGroup = {
  suffix: string;
  suggestions: string[];
};

type Props = {
  groupedSuggestions: SuggestionGroup[];
  keyword: string;
  service: "google" | "amazon" | "youtube" | "rakuten" | "tiktok" | "bing" | "appstore" | "googleplay" | "chiebukuro";
  onKeywordSelect?: (keyword: string) => void;
  onSaveKeyword?: (keyword: string) => void;
  isKeywordSaved?: (keyword: string) => boolean;
};

const LABELS = [
  'ひらがな（前半）',
  'ひらがな（後半）',
  'アルファベット',
  '数字',
] as const;

const SEARCH_URLS = {
  google: (q: string) =>
    `https://www.google.com/search?q=${encodeURIComponent(q)}`,
  amazon: (q: string) =>
    `https://www.amazon.co.jp/s?k=${encodeURIComponent(q)}`,
  youtube: (q: string) =>
    `https://www.youtube.com/results?search_query=${encodeURIComponent(q)}`,
  rakuten: (q: string) =>
    `https://search.rakuten.co.jp/search/mall/${encodeURIComponent(q)}/`,
  tiktok: (q: string) =>
    `https://www.tiktok.com/search?q=${encodeURIComponent(q)}`,
  bing: (q: string) =>
    `https://www.bing.com/search?q=${encodeURIComponent(q)}`,
  appstore: (q: string) =>
    `https://apps.apple.com/jp/search?term=${encodeURIComponent(q)}`,
  googleplay: (q: string) =>
    `https://play.google.com/store/search?q=${encodeURIComponent(q)}&c=apps&hl=ja&gl=JP`,
  chiebukuro: (q: string) =>
    `https://chiebukuro.yahoo.co.jp/search?p=${encodeURIComponent(q)}`
};



const SuggestionList: React.FC<Props> = ({
  groupedSuggestions,
  keyword,
  service,
  onKeywordSelect,
  onSaveKeyword,
  isKeywordSaved,
}) => {
  const [filterText, setFilterText] = React.useState('');

  const filteredGroups = React.useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) return groupedSuggestions;

    return groupedSuggestions
      .map((group) => ({
        ...group,
        suggestions: group.suggestions.filter((word) =>
          word.toLowerCase().includes(query)
        ),
      }))
      .filter((group) => group.suggestions.length > 0);
  }, [filterText, groupedSuggestions]);

  // 分類を計算（フックはトップレベル）
  const categorized = React.useMemo(() => {
    const kana1 = 'あいうえおかきくけこさしすせそたちつてと'.split('');
    const kana2 = 'なにぬねのはひふへほまみむめもやゆよらりるれろわをん'.split('');
    const alphabet = 'abcdefghijklmnopqrstuvwxyz'.split('');
    const numbers = '0123456789'.split('');

    const groups: Record<(typeof LABELS)[number], SuggestionGroup[]> = {
      'ひらがな（前半）': [],
      'ひらがな（後半）': [],
      'アルファベット': [],
      '数字': [],
    };

    for (const group of filteredGroups) {
      const s = group.suffix;
      if (kana1.includes(s)) groups['ひらがな（前半）'].push(group);
      else if (kana2.includes(s)) groups['ひらがな（後半）'].push(group);
      else if (alphabet.includes(s)) groups['アルファベット'].push(group);
      else if (numbers.includes(s)) groups['数字'].push(group);
    }
    return groups;
  }, [filteredGroups]);

  const totalSuggestionCount = React.useMemo(
    () => groupedSuggestions.reduce((sum, group) => sum + group.suggestions.length, 0),
    [groupedSuggestions]
  );

  const filteredSuggestionCount = React.useMemo(
    () => filteredGroups.reduce((sum, group) => sum + group.suggestions.length, 0),
    [filteredGroups]
  );

  if (groupedSuggestions.length === 0) return null;

  return (
  /* ───── カードはこれ 1 枚だけ ───── */
  <div className="bg-white rounded-lg shadow-sm p-8">
    <div className="mb-6 border-b border-gray-200 pb-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">キーワード一覧</h2>
          <p className="mt-1 text-sm text-gray-500">
            含まれる語でリアルタイムに絞り込めます。
          </p>
        </div>
        <div className="text-sm font-semibold text-gray-500">
          {filterText.trim()
            ? `${filteredSuggestionCount} / ${totalSuggestionCount}件`
            : `${totalSuggestionCount}件`}
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100">
        <i className="fa-solid fa-filter text-gray-400"></i>
        <input
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          placeholder="例: 問題"
          className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
        />
        {filterText && (
          <button
            type="button"
            aria-label="絞り込みをクリア"
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            onClick={() => setFilterText('')}
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        )}
      </div>
    </div>

    {filteredGroups.length === 0 ? (
      <div className="rounded border border-dashed border-gray-200 px-4 py-10 text-center text-sm text-gray-500">
        条件に合うキーワードはありません
      </div>
    ) : (
<div className="grid gap-y-6 gap-x-10
                grid-cols-[repeat(auto-fit,minmax(260px,1fr))]">
      {/* ← 以下はカードではなく単なる区画 */}
      {LABELS.map((label) =>
        categorized[label].length === 0 ? null : (
          <section key={label} className="space-y-4">
            <h1 className="text-lg font-bold border-b pb-1">{label}</h1>

            {categorized[label].map((group) => (
              <div key={group.suffix}>
                <h2 className="font-semibold bg-sky-100 text-sky-800 px-2 py-1 rounded mb-1">
                  {keyword}
                  <span className="text-blue-600">+{group.suffix}</span>
                </h2>
<ul className="list-none pl-0 space-y-0.5">
  {group.suggestions.map((word) => {
    
    const searchUrl = SEARCH_URLS[service](word);

    return (
      <li key={word} className="flex items-center justify-between">
        <span>{word}</span>

        <div className="ml-2 flex items-center gap-1">
          {onSaveKeyword && (
            <button
              type="button"
              aria-label={`${word} を保存`}
              title={isKeywordSaved?.(word) ? '保存済み' : '保存する'}
              className={`p-1 rounded-full transition ${
                isKeywordSaved?.(word)
                  ? 'text-amber-500 hover:bg-amber-50'
                  : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50'
              }`}
              onClick={() => onSaveKeyword(word)}
            >
              <i className={`${isKeywordSaved?.(word) ? 'fa-solid' : 'fa-regular'} fa-star`}></i>
            </button>
          )}
          {onKeywordSelect && (
            <button
              type="button"
              aria-label={`${word} で再検索`}
              title="このキーワードで再検索"
              className="p-1 rounded-full text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition"
              onClick={() => onKeywordSelect(word)}
            >
              <i className="fa-solid fa-arrows-rotate"></i>
            </button>
          )}
          <button
            type="button"
            aria-label={`${word} を検索`}
            title="外部検索で開く"
            className="p-1 rounded-full text-sky-600 hover:text-sky-800 hover:bg-gray-200 transition"
            onClick={() =>
              window.open(searchUrl, '_blank', 'noopener,noreferrer')
            }
          >
            <i className="fa-solid fa-magnifying-glass"></i>
          </button>
        </div>
      </li>
    );
  })}
</ul>


              </div>
            ))}
          </section>
        ),
      )}
    </div>
    )}
  </div>
);


};

export default SuggestionList;
